import type { CalendarDayEntry } from '../services/api';
import {
  formatGregorian,
  getDaysInJalaliMonth,
  gregorianWeekdaysToJalali,
  jalaliToDate,
  shiftJalaliMonth,
  todayJalali,
} from './jalali';

const MONTHS_BEFORE = 3;
const MONTHS_AFTER = 1;

export interface CalendarGridItem {
  day: string | number;
  month: number;
  year: number;
  dayOfWeek?: number;
  extra?: CalendarDayEntry;
}

/** Index API rows by Gregorian YYYY-MM-DD (matches backend `day` field). */
export function indexApiDaysByGregorian(apiDays: CalendarDayEntry[]): Map<string, CalendarDayEntry> {
  const map = new Map<string, CalendarDayEntry>();
  for (const entry of apiDays) {
    if (entry?.day) map.set(entry.day, entry);
  }
  return map;
}

function extraForJalaliDay(
  apiByDate: Map<string, CalendarDayEntry>,
  year: number,
  month: number,
  day: number,
): CalendarDayEntry | undefined {
  return apiByDate.get(formatGregorian(jalaliToDate(year, month, day)));
}

/** Builds padded Jalali month grid aligned with API day rows (Ai-Tempo Reset_Calendar parity). */
export function buildCalendarGrid(apiDays: CalendarDayEntry[]): CalendarGridItem[] {
  const today = todayJalali();
  const months: { year: number; month: number }[] = [];
  for (let offset = -MONTHS_BEFORE; offset <= MONTHS_AFTER; offset++) {
    months.push(shiftJalaliMonth(today.year, today.month, offset));
  }

  const apiByDate = indexApiDaysByGregorian(apiDays);
  const result: CalendarGridItem[] = [];

  months.forEach(({ year, month }, idx) => {
    if (idx === 0) {
      const firstDayGr = jalaliToDate(year, month, 1).getDay();
      const padCount = gregorianWeekdaysToJalali(firstDayGr);
      for (let p = 0; p < padCount; p++) {
        result.push({ day: '-', year, month, dayOfWeek: p - 1 });
      }
    }

    const daysInMonth = getDaysInJalaliMonth(year, month);
    for (let day = 1; day <= daysInMonth; day++) {
      result.push({
        day,
        month,
        year,
        dayOfWeek: jalaliToDate(year, month, day).getDay(),
        extra: extraForJalaliDay(apiByDate, year, month, day),
      });
    }
  });

  return result;
}

export function filterListDays(items: CalendarGridItem[]): CalendarGridItem[] {
  return items.filter((d) => d.day !== '-');
}

/** True when at least one real day cell exists for the Jalali month. */
export function hasMonthInGrid(items: CalendarGridItem[], year: number, month: number): boolean {
  return items.some((item) => item.year === year && item.month === month && item.day !== '-');
}

/** Count real (non-pad) day cells for a Jalali month in the grid. */
export function countMonthDaysInGrid(
  items: CalendarGridItem[],
  year: number,
  month: number,
): number {
  return items.filter((item) => item.year === year && item.month === month && item.day !== '-')
    .length;
}

/** True when every day cell in the month has API payload attached. */
export function isMonthDataHydrated(
  items: CalendarGridItem[],
  year: number,
  month: number,
): boolean {
  const expected = getDaysInJalaliMonth(year, month);
  const real = items.filter((item) => item.year === year && item.month === month && item.day !== '-');
  if (real.length !== expected) return false;
  return real.every((item) => item.extra != null && typeof item.extra.day === 'string');
}

/** Builds grid cells for a single Jalali month (leading pad + day rows). */
export function buildSingleMonthGrid(
  year: number,
  month: number,
  apiDays: CalendarDayEntry[],
): CalendarGridItem[] {
  const result: CalendarGridItem[] = [];
  const firstDayGr = jalaliToDate(year, month, 1).getDay();
  const padCount = gregorianWeekdaysToJalali(firstDayGr);
  for (let p = 0; p < padCount; p++) {
    result.push({ day: '-', year, month, dayOfWeek: p - 1 });
  }

  const apiByDate = indexApiDaysByGregorian(apiDays);
  const daysInMonth = getDaysInJalaliMonth(year, month);
  for (let day = 1; day <= daysInMonth; day++) {
    result.push({
      day,
      month,
      year,
      dayOfWeek: jalaliToDate(year, month, day).getDay(),
      extra: extraForJalaliDay(apiByDate, year, month, day),
    });
  }

  return result;
}

function monthSortKey(item: CalendarGridItem): number {
  const dayNum = item.day === '-' ? 0 : Number(item.day);
  return item.year * 10000 + item.month * 100 + dayNum;
}

/** Inserts or refreshes one month in an existing multi-month grid. */
export function mergeMonthIntoGrid(
  existing: CalendarGridItem[],
  year: number,
  month: number,
  apiDays: CalendarDayEntry[],
): CalendarGridItem[] {
  if (!hasMonthInGrid(existing, year, month)) {
    const without = existing.filter((item) => item.year !== year || item.month !== month);
    const merged = [...without, ...buildSingleMonthGrid(year, month, apiDays)];
    return merged.sort((a, b) => monthSortKey(a) - monthSortKey(b));
  }

  const apiByDate = indexApiDaysByGregorian(apiDays);
  return existing.map((item) => {
    if (item.year !== year || item.month !== month || item.day === '-') return item;
    const dayNum = Number(item.day);
    const extra = extraForJalaliDay(apiByDate, year, month, dayNum);
    return extra != null ? { ...item, extra } : item;
  });
}
